import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import useNotificationStore, {
  type Notification,
  notificationLink,
} from "@/pages/profile/store/notificationStore";

/** How many notifications the dropdown shows before "View all". */
const RECENT_LIMIT = 7;

/** How often the unread badge refreshes while the page is open. */
const REFRESH_MS = 60_000;

const timeAgo = (iso: string) => {
  const diffMins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * Header bell: unread badge plus a dropdown of the latest notifications. Opening
 * one marks it read and goes to what it points at; "View all" opens the full
 * notifications page.
 */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { recent, unreadCount, getRecentNotifications, markAsRead, markAllAsRead } =
    useNotificationStore(
      useShallow((state) => ({
        recent: state.recent,
        unreadCount: state.unreadCount,
        getRecentNotifications: state.getRecentNotifications,
        markAsRead: state.markAsRead,
        markAllAsRead: state.markAllAsRead,
      })),
    );

  // Keep the badge current without a socket: load now, refresh while the tab
  // is visible, and again whenever it becomes visible.
  useEffect(() => {
    const load = () =>
      getRecentNotifications(RECENT_LIMIT).catch(() => {
        // A failed refresh leaves the last known list; the next one retries.
      });
    // Background polling only runs while the tab is visible; the first load
    // always runs, so the badge is right as soon as the page appears.
    const refresh = () => {
      if (document.visibilityState === "visible") load();
    };
    load();
    const timer = window.setInterval(refresh, REFRESH_MS);
    // A tab opened in the background loads its badge as soon as it is shown.
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [getRecentNotifications]);

  useEffect(() => {
    if (!open) return;
    getRecentNotifications(RECENT_LIMIT).catch(() => {});

    const onClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open, getRecentNotifications]);

  const openNotification = (notification: Notification) => {
    if (!notification.is_read) markAsRead(notification.id);
    setOpen(false);
    navigate(notificationLink(notification) ?? "/notification");
  };

  const badge = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        aria-label={
          unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"
        }
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-white/10"
      >
        <Bell className="h-5 w-5 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-[#EA33C0] bg-white px-1 text-[10px] font-bold leading-none text-[#EA33C0]">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">Notifications</p>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead().catch(() => {})}
                className="text-xs font-medium text-[#EA33C0] hover:underline"
              >
                Mark all as read
              </button>
            )}
          </div>

          {recent.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">
              You have no notifications yet.
            </p>
          ) : (
            <ul className="max-h-[420px] overflow-y-auto">
              {recent.map((notification) => (
                <li key={notification.id}>
                  <button
                    role="menuitem"
                    onClick={() => openNotification(notification)}
                    className={`flex w-full gap-3 border-b border-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                      notification.is_read ? "" : "bg-pink-50/60"
                    }`}
                  >
                    <span
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                        notification.is_read ? "bg-transparent" : "bg-[#EA33C0]"
                      }`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {notification.display_title}
                      </span>
                      <span className="line-clamp-2 block text-sm text-gray-600">
                        {notification.display_message}
                      </span>
                      <span className="mt-1 block text-xs text-gray-400">
                        {timeAgo(notification.created_at)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            onClick={() => {
              setOpen(false);
              navigate("/notification");
            }}
            className="block w-full border-t border-gray-100 px-4 py-3 text-center text-sm font-medium text-[#EA33C0] hover:bg-gray-50"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
